<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const conditions = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

// Stable key per item for the draggable list — using the editable text as the
// key would recreate the <input> DOM node (and drop focus) on every keystroke.
// The key lives in a WeakMap, so it never becomes part of the saved payload.
const itemKeys = new WeakMap();
let nextKey = 0;
const itemKey = condition => {
  if (!itemKeys.has(condition)) {
    nextKey += 1;
    itemKeys.set(condition, nextKey);
  }
  return itemKeys.get(condition);
};

const addCondition = () => {
  emit('update:modelValue', [...props.modelValue, { text: '', enabled: true }]);
};

const removeCondition = index => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, position) => position !== index)
  );
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <label v-if="label" class="text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <Draggable
      v-model="conditions"
      tag="div"
      :item-key="itemKey"
      class="flex flex-col gap-2"
      handle=".drag-handle"
    >
      <template #item="{ element: condition, index }">
        <div class="flex items-center gap-2">
          <Icon
            icon="i-lucide-grip-vertical"
            class="drag-handle size-4 text-n-slate-11 cursor-move flex-shrink-0"
          />
          <Switch v-model="condition.enabled" />
          <Input
            v-model="condition.text"
            class="flex-1"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.CONDITIONS.PLACEHOLDER')
            "
          />
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="removeCondition(index)"
          />
        </div>
      </template>
    </Draggable>
    <Button
      icon="i-lucide-plus"
      variant="ghost"
      size="sm"
      class="!w-fit !text-n-brand hover:!text-n-brand"
      :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.CONDITIONS.ADD')"
      @click="addCondition"
    />
  </div>
</template>
